<template>
  <system-frame title="Pääsuvõtmed" section="Turvalisus" hint="Sisselogimine telefoni või arvuti pääsuvõtmega. Iga grupp lülitub eraldi.">
    <!-- server/controllers/passkeys.js; what the switches do is in
         server/service/passkey.js and auth/passkey.js. -->
    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert>

    <card v-for="group in groups" :key="group.on" class="mb-6">
      <div class="d-flex align-start ga-4">
        <div class="flex-grow-1">
          <div class="text-h6">{{ group.title }}</div>
          <p class="text-body-2 text-grey-darken-2 mb-0">{{ group.text }}</p>
        </div>
        <v-switch
          :model-value="settings[group.on]"
          :aria-label="group.title + ' sisse'"
          color="green"
          inset
          hide-details
          :disabled="saving || !loaded"
          @update:model-value="save({ [group.on]: $event })"
        />
      </div>

      <div class="d-flex align-center flex-wrap ga-4 mt-4">
        <span class="text-caption text-uppercase font-weight-bold text-grey-darken-1">Kasutamine</span>
        <!-- Two plain buttons, not v-btn-toggle: a toggle bound to a value it
             does not own swallowed the click, and Kohustuslik never asked. -->
        <div class="pk-seg" role="group" :aria-label="group.title + ': kasutamine'">
          <button
            v-for="choice in group.choices"
            :key="choice.value"
            type="button"
            class="pk-seg__btn"
            :class="{ on: mode(group) === choice.value }"
            :aria-pressed="mode(group) === choice.value"
            :disabled="saving || !loaded"
            @click="pick(group, choice.value)"
          >{{ choice.text }}</button>
        </div>
        <span v-if="group.count" class="text-body-2 text-grey-darken-2">{{ group.count }}</span>
      </div>

      <p class="text-body-2 text-grey-darken-2 mt-3 mb-0">
        <template v-if="!settings[group.on]">Väljas: sisselogimislehel pole nuppu ega võtmete loendit. Lisatud võtmed jäävad alles, aga ei tööta.</template>
        <template v-else-if="mode(group) === 'required'">{{ group.requiredText }}</template>
        <template v-else-if="mode(group) === 'keyOnly'">{{ group.keyOnlyText }}</template>
        <template v-else>{{ group.optionalText }}</template>
      </p>

      <!-- Anything stricter asks first: it changes how people log in. -->
      <v-alert v-if="asking && asking.group === group.on" type="warning" variant="tonal" class="mt-4">
        <div class="mb-3">{{ asking.value === 'keyOnly' ? group.askKeyOnlyText : group.askText }}</div>
        <v-btn color="green" class="mr-2" :loading="saving" @click="saveMode(group, asking.value)">{{ asking.value === 'keyOnly' ? 'Jah, keela võtmega kasutajate parool' : 'Jah, tee kohustuslikuks' }}</v-btn>
        <v-btn variant="text" @click="asking = null">Tühista</v-btn>
      </v-alert>
    </card>

    <card>
      <div class="text-h6 mb-2">Kui keegi ei pääse sisse</div>
      <ul class="text-body-2 pl-4">
        <li>Kadunud telefon: kasutaja kustutab selle võtme teisest seadmest, või admin teeb seda Kasutajad → kasutaja lehel. Kliendi võtmed kustutab kontor kliendi lehel.</li>
        <li>Root saab alati parooliga sisse, ka siis, kui teistel on parool keelatud.</li>
        <li>Võti töötab ainult sellel aadressil, kus see lisati ({{ host }}).</li>
      </ul>
    </card>
  </system-frame>
</template>

<script>
import api from '@/api'
import SystemFrame from '@/components/SystemFrame.vue'

export default {
  components: { SystemFrame },
  data () {
    return {
      settings: { passkeyUser: false, passkeyUserRequired: false, passkeyUserNoPassword: false, passkeyClient: false, passkeyClientRequired: false },
      counts: null,
      loaded: false,
      saving: false,
      asking: null,
      error: '',
      host: window.location.host
    }
  },
  computed: {
    groups () {
      const users = this.counts && this.counts.users
      const clients = this.counts && this.counts.clients
      const missing = users ? users.total - users.withKey : 0
      return [{
        on: 'passkeyUser',
        required: 'passkeyUserRequired',
        keyOnly: 'passkeyUserNoPassword',
        choices: [{ value: 'optional', text: 'Valikuline' }, { value: 'keyOnly', text: 'Võtmega parool keelatud' }, { value: 'required', text: 'Kohustuslik' }],
        title: 'Intra kasutajad',
        text: 'Intra ja aktide rakendus. Võtmed lisab igaüks ise: Minu seaded → Pääsuvõtmed.',
        count: users ? 'Võti on ' + users.withKey + ' aktiivsel kasutajal ' + users.total + '-st' : '',
        optionalText: 'Valikuline: igaüks saab võtme lisada, parool töötab edasi.',
        keyOnlyText: 'Võtmega parool keelatud: kes on võtme lisanud, logib sisse ainult võtmega. Võtmeta kasutaja logib edasi parooliga ja keegi ei sunni teda võtit lisama. Root saab alati parooliga sisse.',
        askKeyOnlyText: (users ? users.withKey : 0) + ' kasutajal on võti: nad ei saa enam parooliga sisse, ka mitte seadmest, millel võtit pole. Kes võtme hiljem lisab, kaotab parooli samamoodi. Võtmeta kasutajate jaoks ei muutu midagi.',
        requiredText: 'Kohustuslik: võtmeta kasutaja peab pärast parooliga sisselogimist kohe võtme lisama. Võtmega kasutaja parool üksi enam sisse ei lase. Root saab alati parooliga sisse.',
        askText: missing > 0
          ? missing + ' kasutajal pole veel võtit. Nende järgmine klõps viib lehele „Lisa pääsuvõti” ja muud nad enne ei näe. Kellel võti on, see ei saa enam parooliga sisse.'
          : 'Kõigil aktiivsetel kasutajatel on võti. Edaspidi parool üksi sisse ei lase (root välja arvatud).'
      }, {
        on: 'passkeyClient',
        required: 'passkeyClientRequired',
        choices: [{ value: 'optional', text: 'Valikuline' }, { value: 'required', text: 'Kohustuslik' }],
        title: 'Kliendiportaal',
        text: 'Kliendid lepingunumbri ja telefoni asemel. Võtmed lisab klient portaalis ise; kontor näeb ja kustutab neid kliendi lehel.',
        count: clients ? 'Võti on ' + clients.withKey + ' kliendil' : '',
        optionalText: 'Valikuline: klient saab võtme lisada, lepingunumber ja telefon töötavad edasi.',
        requiredText: 'Kohustuslik: klient, kellel võtit pole, peab pärast sisselogimist võtme lisama. Võtmega klient ei saa enam telefoninumbriga sisse.',
        askText: 'Iga klient, kellel võtit pole, peab järgmisel sisselogimisel võtme lisama, enne kui midagi näeb. Telefonita või vanema telefoniga kliendid võivad hätta jääda.'
      }]
    }
  },
  async mounted () {
    try {
      this.apply(await api.getPasskeySettings())
    } catch (err) {
      this.error = err.message
    }
  },
  methods: {
    apply (data) {
      this.settings = data.settings
      this.counts = data.counts
      this.loaded = true
    },
    // Kohustuslik wins if both are ever set: it is the stricter of the two.
    mode (group) {
      if (this.settings[group.required]) return 'required'
      return group.keyOnly && this.settings[group.keyOnly] ? 'keyOnly' : 'optional'
    },
    pick (group, value) {
      if (value === this.mode(group)) return
      if (value === 'optional') {
        this.asking = null
        this.saveMode(group, value)
      } else {
        this.asking = { group: group.on, value }
      }
    },
    saveMode (group, value) {
      this.save({
        [group.required]: value === 'required',
        ...(group.keyOnly ? { [group.keyOnly]: value === 'keyOnly' } : {})
      })
    },
    async save (change) {
      this.saving = true
      this.error = ''
      try {
        this.apply(await api.savePasskeySettings(change))
        this.asking = null
      } catch (err) {
        this.error = err.message
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.pk-seg { display: inline-flex; flex-wrap: wrap; max-width: 100%; border: 1.5px solid #9AAAA4; border-radius: 7px; overflow: hidden; }
.pk-seg__btn { padding: 7px 16px; font: inherit; font-size: 14px; font-weight: 600; background: #fff; color: #16322A; border: 0; cursor: pointer; }
.pk-seg__btn + .pk-seg__btn { border-left: 1.5px solid #9AAAA4; }
.pk-seg__btn.on { background: #16322A; color: #fff; }
.pk-seg__btn:disabled { opacity: .5; cursor: default; }
.pk-seg__btn:focus-visible { outline: 3px solid #1F5FD1; outline-offset: -3px; }
</style>
